<script>
import axios from 'axios';
import { extend } from 'vee-validate';
import { checkIsBannedPwd } from '../../../lib/api/checkIsBannedPwd';
import moment from 'moment';
/**
 * @description Resource management mixin used for generating an update patch and  handling policy errors
 * -
 **/
export default {
    name: 'Input-Validations',
    data () {
        return {
            userValidationCache: {
                lastCheckedUid: null,
                result: null
            },
            lastPoaEmailValue: null
        };
    },
    methods: {
        customValidations () {
            extend('regSpecialChar', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.specialCheck'),
                validate: value => {
                    return this.regRegexSpecialChar(value);
                }
            });

            extend('dob', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.dobCheck'),
                validate: value => {
                    return moment(value, 'MM-YYYY', true).isValid() && moment(value, 'MM-YYYY', true).isBefore(moment().subtract(1, 'months'));
                }
            });

            extend('regUNSpaceChk', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.unSpaceCheck'),
                validate: value => {
                    return this.regSpaceChk(value);
                }
            });

            extend('regFNSpaceChk', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.fnSpaceCheck'),
                validate: value => {
                    return this.regSpaceChk(value);
                }
            });

            extend('regLNSpaceChk', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.lnSpaceCheck'),
                validate: value => {
                    return this.regSpaceChk(value);
                }
            });

            extend('email', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.validEmail'),
                validate: value => {
                    return this.regRegexEmail(value);
                }
            });

            extend('regPearsonEmail', {
                message: field => this.$t('pages.selfservice.registration.inputValidationMsg.notPermittedPearsonUser'),
                validate: value => {
                    if (value.toString().substring(value.indexOf('@'), value.toString().length + 1).toLowerCase() === '@pearson.com') {
                        return false;
                    } else {
                        return true;
                    }
                }
            });

            extend('emailIsNotSnowflake', {
                validate: value => {
                    return !value.toLowerCase().endsWith('@snowflake.com');
                },
                message: () => ({
                    __html: this.$t('pages.selfservice.registration.inputValidationMsg.snowflakeNotAllowed')
                })
            });

            const checkUsernameStatus = (value) => {
                const config = {
                    headers: { 'x-openidm-password': 'anonymous', 'x-openidm-username': 'anonymous' }
                };

                if (this.saveDetails.uid !== '' && this.regRegexSpecialChar(this.saveDetails.uid)) {
                    if (this.userValidationCache.lastCheckedUid === this.saveDetails.uid && this.userValidationCache.result !== null) {
                        return Promise.resolve(this.userValidationCache.result);
                    }

                    return axios.get('/openidm/endpoint/checkUsernameAvailability/' + this.saveDetails.uid + '/?isSocial=true', config)
                        .then((response) => {
                            this.userValidationCache.lastCheckedUid = this.saveDetails.uid;
                            this.userValidationCache.result = response;
                            return response;
                        })
                        .catch((error) => {
                            return {
                                data: {
                                    isUsernameAvailable: false,
                                    isSocial: false,
                                    isAccountLinkingEnabled: false,
                                    _id: null,
                                    error: true,
                                    errorMessage: error.response?.data?.message || 'Error checking username availability'
                                }
                            };
                        });
                } else {
                    return Promise.resolve({
                        data: {
                            isUsernameAvailable: false,
                            _id: null
                        }
                    });
                }
            };

            const isUserAvailable = (value) => {
                if (this.saveDetails.uid === '' || !this.regRegexSpecialChar(this.saveDetails.uid)) {
                    return {
                        valid: false, // or false
                        data: {
                            required: true // or false
                        }
                    };
                }

                return checkUsernameStatus(value).then((response) => {
                    return {
                        valid: response.data.isUsernameAvailable,
                        data: {
                            message: response.data._id
                        }
                    };
                });
            };

            const isUserAccountLinking = (value) => {
                if (this.saveDetails.uid === '' || !this.regRegexSpecialChar(this.saveDetails.uid)) {
                    return {
                        valid: false, // or false
                        data: {
                            required: true // or false
                        }
                    };
                }

                return checkUsernameStatus(value).then((response) => {
                    if (response.data.error) {
                        return {
                            valid: true,
                            data: {
                                message: response.data.errorMessage
                            }
                        };
                    }

                    return {
                        valid: response.data.isUsernameAvailable || !response.data.isSocial || !response.data.isAccountLinkingEnabled,
                        data: {
                            message: response.data._id
                        }
                    };
                });
            };

            extend('regUserAvailable', {
                validate: isUserAvailable,
                message: () => {
                    if (this.accountLinkingMessage &&
                        ((typeof this.accountLinkingMessage === 'object' && this.accountLinkingMessage.__html) ||
                         (typeof this.accountLinkingMessage === 'string' && this.accountLinkingMessage !== ''))) {
                        return '';
                    }
                    return this.$t('pages.selfservice.registration.inputValidationMsg.usernameAlreadyExist');
                }
            });

            extend('isAccountLinking', {
                validate: async (value) => {
                    const result = await isUserAccountLinking(value);
                    if (!result.valid) {
                        const encodedUid = btoa(encodeURIComponent(this.saveDetails.uid || ''));
                        const socialProvider = this.userValidationCache.result?.data?.socialProvider || '';
                        let gotoParam = '';
                        if (this.$route && this.$route.query && this.$route.query.goto) {
                            gotoParam = this.$route.query.goto;
                        } else {
                            const urlParams = new URLSearchParams(window.location.search);
                            const gotoParams = urlParams.getAll('goto');
                            if (gotoParams.length > 0) {
                                gotoParam = gotoParams[gotoParams.length - 1];
                            } else if (window.location.hash) {
                                const hashParts = window.location.hash.split('?');
                                if (hashParts.length > 1) {
                                    const hashParams = new URLSearchParams(hashParts[1]);
                                    const hashGotoParams = hashParams.getAll('goto');
                                    if (hashGotoParams.length > 0) {
                                        gotoParam = hashGotoParams[hashGotoParams.length - 1];
                                    }
                                }
                            }
                        }
                        let decodedGoto = '';
                        // Check if the goto parameter contains SAMLRequest
                        if (gotoParam && gotoParam.includes('SAMLRequest')) {
                            console.log('SAMLRequest found in goto parameter.');
                            console.log('gotoParam before decoding:', gotoParam);
                            // Decode the URL only if SAMLRequest is present
                            decodedGoto = encodeURIComponent(decodeURIComponent(gotoParam));
                            console.log('decodedGoto after decoding:', decodedGoto);
                        } else {
                            // Use the goto parameter as-is to avoid double-encoding
                            decodedGoto = encodeURIComponent(gotoParam);
                        }
                        console.log('Account linking goto parameter:', gotoParam);
                        console.log('Account linking decoded goto:', decodedGoto);

                        const message = this.$t('pages.selfservice.registration.inputValidationMsg.accountLinking')
                            .replace('{uid}', encodedUid)
                            .replaceAll('{socialProvider}', socialProvider)
                            .replaceAll('{goto}', decodedGoto);

                        if (this.accountLinkingMessage !== undefined) {
                            this.accountLinkingMessage = message;
                        }
                    } else {
                        if (this.accountLinkingMessage !== undefined) {
                            this.accountLinkingMessage = '';
                        }
                    }
                    return true;
                },
                message: () => ''
            });

            extend('pwdSpecialChar', {
                message: () => this.$t('pages.selfservice.passwordReset.specChar'),
                validate: value => {
                    return this.pwdRegexSpecialChar(value);
                }
            });

            extend('pwdComplexPassword', {
                message: () => this.$t('pages.selfservice.passwordReset.complexPassword'),
                validate: value => {
                    return this.pwdRegexComplexPassword(value);
                }
            });

            extend('pwdIsUniqueforpobl', {
                message: () => this.$t('pages.selfservice.passwordReset.isUnique'),
                validate: value => {
                    return this.pwdIsUnique(value, 'pwdIsUniqueforpobl');
                }
            });

            extend('pwdIsUniqueComplexPassword', {
                message: () => this.$t('pages.selfservice.passwordReset.isUniqueComplexPassword'),
                validate: value => {
                    return this.pwdIsUnique(value, 'pwdIsUniqueComplexPassword');
                }
            });

            extend('pwdIsNotInBannedList', {
                message: () => this.$t('pages.selfservice.registration.inputValidationMsg.pwdIsNotInBannedList'),
                validate: async (value) => {
                    const response = await checkIsBannedPwd(value);
                    return response.ok;
                }
            });
            extend('minChars', {
                message: () => this.$t('pages.selfservice.registration.inputValidationMsg.pwdMinChars'),
                validate: (value) => value.length >= 8
            }
            );

            // POA Client Email Validation
            const isPOAEmailCheck = (value) => {
                if (!this.poaClient) {
                    return { valid: true };
                }

                const config = {
                    headers: {
                        'Content-Type': 'application/json',
                        'x-openidm-username': 'anonymous',
                        'x-openidm-password': 'anonymous'
                    }
                };

                if (value === this.userValidationCache.lastCheckedUid) {
                    return { valid: this.userValidationCache.result };
                }

                // Track the email being validated
                this.userValidationCache.lastCheckedUid = value;

                if (value && this.regRegexEmail(value)) {
                    return axios.get(`/openidm/endpoint/checkUsernameAvailability/${value}?isEmail=true`, config)
                        .then((response) => {
                            const { isUsernameAvailable, isEmailExist } = response.data;
                            // If username is not available, show error
                            if (!isUsernameAvailable) {
                                this.userValidationCache.result = false;
                                // Clear warning for error states
                                if (this.showEmailWarning !== undefined) {
                                    this.showEmailWarning = false;
                                    this.emailWarningMessage = '';
                                }
                                return {
                                    valid: false,
                                    data: {
                                        message: response.data._id
                                    }
                                };
                            } else {
                                this.userValidationCache.result = true;
                            }
                            // If username is available but email exists, show warning
                            if ((isUsernameAvailable && isEmailExist)) {
                                const forgotUsernameLink = this.buildForgotUsernameLink();
                                const forgotUsernameLinkText = this.$t('pages.selfservice.registration.inputValidationMsg.forgotUsernameLink');
                                const linkHtml = `<a href="${forgotUsernameLink}" target="_blank" style="color: #ffc107; text-decoration: underline;">${forgotUsernameLinkText}</a>`;
                                const warningMessage = this.$t('pages.selfservice.registration.inputValidationMsg.poaEmailWarning', { forgotUsernameLink: linkHtml });
                                if (this.emailWarningMessage !== undefined) {
                                    this.emailWarningMessage = warningMessage;
                                    this.showEmailWarning = true;
                                }
                            } else {
                                // Only clear warning if email doesn't exist
                                if (this.showEmailWarning !== undefined) {
                                    this.showEmailWarning = false;
                                    this.emailWarningMessage = '';
                                }
                            }
                            // Always return valid for POA since this is just informational
                            return {
                                valid: true,
                                data: {
                                    message: response.data._id
                                }
                            };
                        })
                        .catch((error) => {
                            console.error('POA email check error:', error);
                            // Clear warning for error states
                            if (this.showEmailWarning !== undefined) {
                                this.showEmailWarning = false;
                                this.emailWarningMessage = '';
                            }
                            // Don't fail validation on API error
                            return { valid: true };
                        });
                } else {
                    // Clear warning if email is invalid or empty
                    if (this.showEmailWarning !== undefined) {
                        this.showEmailWarning = false;
                        this.emailWarningMessage = '';
                    }
                    this.userValidationCache.lastCheckedUid = null;
                    this.userValidationCache.result = null;
                    return { valid: true };
                }
            };

            extend('poaEmailCheck', {
                validate: isPOAEmailCheck,
                message: () => this.$t('pages.selfservice.registration.inputValidationMsg.usernameAlreadyExist')
            });
        },

        regRegexSpecialChar (value) {
            if (value !== '') {
                return !(/[^a-zA-ZÀ-ÿœŒ«»€0-9\-_. @',$+\u0600-\u06FF]/.test(value));
            } else {
                return false;
            }
        },

        regSpaceChk (value) {
            if (value.indexOf(' ') === 0 || (value.lastIndexOf(' ') === value.length - 1 && value.lastIndexOf(' ') !== -1)) {
                return false;
            } else {
                return true;
            }
        },

        regRegexEmail (value) {
            var pattern = /^(?=.{7,128}$)(?:[A-Za-z0-9-#$%&'*+/=?^_`{|}~]+\.)*(?:[A-Za-z0-9#$%&'*+/=?^_`{|}~-])+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+(?:[a-zA-Z]{2,63})$/i;
            return !(!value.match(pattern));
        },

        pwdRegexSpecialChar (value) {
            return !(/[^a-zA-Z0-9`!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>//?~]/.test(value));
        },

        pwdRegexComplexPassword (value) {
            var strongRegex = new RegExp('^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
            return (strongRegex.test(value));
        },

        pwdIsUnique (value, type) {
            var IsUniqueUID = (value.toLowerCase().indexOf(this.getValueFromReq(this.selfServiceDetails.requirements.uid, !this.isNullorEmptyorUnde(this.saveDetails) ? this.saveDetails.uid : '')) !== -1), returnIsUnique = IsUniqueUID;
            if (type === 'pwdIsUniqueforpobl') {
                returnIsUnique = IsUniqueUID || (value.toLowerCase().indexOf(this.getValueFromReq(this.selfServiceDetails.requirements.firstname, !this.isNullorEmptyorUnde(this.saveDetails) ? this.saveDetails.givenname : '')) !== -1) || (value.toLowerCase().indexOf(this.getValueFromReq(this.selfServiceDetails.requirements.lastname, !this.isNullorEmptyorUnde(this.saveDetails) ? this.saveDetails.sn : '')) !== -1);
            }
            return !returnIsUnique;
        },

        isNullorEmptyorUnde (value) {
            return value === null || value === '' || value === undefined;
        },

        getValueFromReq (resetPassValue, selfRegValue) {
            var returnValue = null;
            if (!this.isNullorEmptyorUnde(resetPassValue)) {
                returnValue = resetPassValue;
            } else if (!this.isNullorEmptyorUnde(selfRegValue)) {
                returnValue = selfRegValue;
            }
            return !this.isNullorEmptyorUnde(returnValue) ? returnValue.toLowerCase() : returnValue;
        },

        buildForgotUsernameLink () {
            const hostname = window.location.hostname;
            let baseUrl = `https://${hostname}/identity/#/forgotUsername/`;

            // Get current URL parameters to preserve them
            const urlParams = new URLSearchParams(window.location.search);
            const gotoParam = urlParams.get('goto') || (this.$route && this.$route.query && this.$route.query.goto);

            if (gotoParam) {
                const encodedGoto = encodeURIComponent(gotoParam);
                baseUrl += `?goto=${encodedGoto}`;

                // Preserve other query parameters
                const otherParams = [];
                urlParams.forEach((value, key) => {
                    if (key !== 'goto') {
                        otherParams.push(`${key}=${encodeURIComponent(value)}`);
                    }
                });

                if (otherParams.length > 0) {
                    baseUrl += '&' + otherParams.join('&');
                }
            }

            return baseUrl;
        }
    }
};
</script>
